import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Download, Mail, ExternalLink, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMsg } from "@/lib/api";

const formatBRL = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PropostaModal({ card, open, onClose, onGerada }) {
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({}); // { codigo: valor(string) }
  const [validadeDias, setValidadeDias] = useState(30);
  const [prazoMeses, setPrazoMeses] = useState(5);
  const [parcelas, setParcelas] = useState(12);
  const [observacoes, setObservacoes] = useState("");
  const [resultado, setResultado] = useState(null);
  const [emailPara, setEmailPara] = useState("");
  const [ccVend, setCcVend] = useState(true);
  const [emailMsg, setEmailMsg] = useState("");
  const [busyEmail, setBusyEmail] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected({});
    setResultado(null);
    setObservacoes("");
    setEmailPara(card?.contato_email || "");
    setEmailMsg("");
    setValidadeDias(30);
    setPrazoMeses(5);
    setParcelas(12);
    setLoading(true);
    api.get("/cel/catalogo")
      .then((r) => setCatalogo(r.data?.produtos || []))
      .catch((e) => toast.error(apiErrorMsg(e)))
      .finally(() => setLoading(false));
  }, [open, card?.contato_email]);

  const total = useMemo(
    () => Object.values(selected).reduce((s, v) => s + (Number(v) || 0), 0),
    [selected]
  );
  const parcelaVal = useMemo(
    () => (total > 0 && parcelas > 0 ? total / Number(parcelas) : 0),
    [total, parcelas]
  );

  const toggleProduto = (codigo) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (codigo in next) delete next[codigo];
      else next[codigo] = "";
      return next;
    });
  };
  const setValor = (codigo, v) => setSelected((prev) => ({ ...prev, [codigo]: v }));

  const gerar = async () => {
    const produtos = Object.entries(selected).map(([codigo, valor]) => ({ codigo, valor: Number(valor) || 0 }));
    if (produtos.length === 0) { toast.error("Selecione ao menos um produto."); return; }
    if (produtos.some((p) => !(p.valor > 0))) { toast.error("Informe o valor de cada produto selecionado."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/cel/indicacoes/${card.id}/proposta`, {
        produtos,
        validade_dias: Number(validadeDias) || 30,
        prazo_execucao_meses: Number(prazoMeses) || 5,
        parcelas: Number(parcelas) || 12,
        observacoes: observacoes.trim() || null,
      });
      setResultado(r.data);
      toast.success(`Proposta gerada · ${formatBRL(r.data.valor_total)}. Card movido para Proposta.`);
      onGerada?.(r.data);
    } catch (e) { toast.error(apiErrorMsg(e)); }
    finally { setBusy(false); }
  };

  const enviarEmail = async () => {
    if (!emailPara.trim()) { toast.error("Informe o e-mail do destinatário."); return; }
    setBusyEmail(true);
    try {
      const r = await api.post(`/cel/indicacoes/${card.id}/proposta/enviar-email`, {
        email_para: emailPara.trim(),
        cc_vendedora: ccVend,
        mensagem_personalizada: emailMsg.trim() || null,
      });
      toast.success(`E-mail enviado para: ${r.data.destinatarios.join(", ")}`);
    } catch (e) { toast.error(apiErrorMsg(e)); }
    finally { setBusyEmail(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[92vh] p-0 gap-0 flex flex-col" data-testid="proposta-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-sky-600" /> Gerar proposta comercial
          </DialogTitle>
          <DialogDescription className="text-xs">
            <b>{card?.municipio_nome}/{card?.uf}</b> · {card?.tipo_orgao || "Câmara"}. Selecione os produtos, informe o valor de cada um. O card vai para <b>Proposta</b> automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading && (
            <div className="text-center py-10 text-slate-400 text-sm">
              <Loader2 className="inline w-4 h-4 mr-1 animate-spin" /> Carregando catálogo...
            </div>
          )}

          {!loading && !resultado && (
            <>
              <div className="mb-3">
                <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wider mb-2">Produtos disponíveis</div>
                <div className="space-y-1.5">
                  {catalogo.map((prod) => {
                    const sel = prod.codigo in selected;
                    return (
                      <div
                        key={prod.codigo}
                        className={`border rounded-lg p-3 transition ${sel ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}
                        data-testid={`prod-${prod.codigo}`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={sel}
                            onCheckedChange={() => toggleProduto(prod.codigo)}
                            className="mt-1"
                            data-testid={`check-${prod.codigo}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-800 text-sm">{prod.nome}</div>
                          </div>
                          {sel && (
                            <div className="w-40">
                              <div className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">Valor R$</div>
                              <Input
                                type="number"
                                min="0"
                                step="1000"
                                value={selected[prod.codigo]}
                                onChange={(e) => setValor(prod.codigo, e.target.value)}
                                className="h-8"
                                placeholder="0"
                                data-testid={`valor-${prod.codigo}`}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-4">
                <div>
                  <Label className="text-xs">Validade (dias)</Label>
                  <Input type="number" min="1" max="180" value={validadeDias} onChange={(e) => setValidadeDias(e.target.value)} data-testid="prop-validade" />
                </div>
                <div>
                  <Label className="text-xs">Prazo execução (meses)</Label>
                  <Input type="number" min="1" max="24" value={prazoMeses} onChange={(e) => setPrazoMeses(e.target.value)} data-testid="prop-prazo" />
                </div>
                <div>
                  <Label className="text-xs">Parcelas</Label>
                  <Input type="number" min="1" max="60" value={parcelas} onChange={(e) => setParcelas(e.target.value)} data-testid="prop-parcelas" />
                </div>
              </div>

              <div className="mt-3">
                <Label className="text-xs">Observações internas (opcional)</Label>
                <Textarea rows={2} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} data-testid="prop-obs" />
              </div>

              {total > 0 && (
                <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap justify-between items-center gap-3" data-testid="prop-total">
                  <div>
                    <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Total estimado</div>
                    <div className="text-2xl font-bold text-slate-900">{formatBRL(total)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{parcelas || 12}x parcelas</div>
                    <div className="text-lg font-bold text-sky-700">{formatBRL(parcelaVal)}</div>
                  </div>
                </div>
              )}
            </>
          )}

          {resultado && (
            <div className="space-y-4" data-testid="proposta-resultado">
              <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-emerald-900">Proposta gerada com sucesso!</div>
                  <div className="text-sm text-emerald-800 mt-1">Total: <b>{formatBRL(resultado.valor_total)}</b> · Card movido para <b>Proposta</b>.</div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-100">
                <iframe src={resultado.url_publica} title="Preview da proposta" className="w-full" style={{ height: "460px", border: 0 }} />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => window.open(resultado.url_publica, "_blank")} data-testid="btn-abrir-pdf">
                  <ExternalLink className="w-4 h-4 mr-1" /> Abrir em nova aba
                </Button>
                <a href={resultado.url_publica} download={`Proposta CEAP - ${card?.municipio_nome}-${card?.uf}.pdf`}>
                  <Button variant="outline" data-testid="btn-baixar-pdf">
                    <Download className="w-4 h-4 mr-1" /> Baixar PDF
                  </Button>
                </a>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wider mb-2 flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> Enviar por e-mail
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">E-mail do destinatário</Label>
                    <Input type="email" value={emailPara} onChange={(e) => setEmailPara(e.target.value)} placeholder="contato@camara.gov.br" data-testid="email-para" />
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                      <Checkbox checked={ccVend} onCheckedChange={(v) => setCcVend(!!v)} data-testid="email-cc" />
                      Enviar cópia para a vendedora responsável
                    </label>
                  </div>
                </div>
                <div className="mt-2">
                  <Label className="text-xs">Mensagem personalizada (opcional)</Label>
                  <Textarea rows={2} value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} placeholder="Ex.: Conforme conversamos ontem, segue nossa proposta..." data-testid="email-msg" />
                </div>
                <Button
                  className="mt-3 bg-sky-600 hover:bg-sky-700 text-white"
                  onClick={enviarEmail}
                  disabled={busyEmail}
                  data-testid="btn-enviar-email"
                >
                  <Mail className="w-4 h-4 mr-1" /> {busyEmail ? "Enviando..." : "Enviar e-mail"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100 flex-shrink-0 bg-white">
          <Button variant="outline" onClick={() => onClose?.()} disabled={busy}>
            {resultado ? "Fechar" : "Cancelar"}
          </Button>
          {!resultado && (
            <Button
              onClick={gerar}
              disabled={busy || total === 0 || loading}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="btn-gerar-proposta-submit"
            >
              <FileText className="w-4 h-4 mr-1" />
              {busy ? "Gerando..." : `Gerar proposta · ${formatBRL(total)}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
